// Two guards for values this site renders but does not own.
//
// Blog authors self-service their own profile (`PUT /blog/me/profile`, backend
// app/api/v1/blog/me.py). `AuthorProfileIn` validates lengths and runs
// `_clean_url` over the four URL fields — but nothing sanitises the free-text
// ones, and `put_my_profile` does not reset `is_visible`, so an author whose
// profile a super admin approved once keeps editing it while staying public.
// Everything below therefore treats author-supplied strings as hostile.

/** `<` and the two line separators, with their JSON-safe escapes.
    Built with fromCharCode so U+2028/U+2029 never appear literally in this
    file — an invisible character in source is a trap for the next editor. */
const SEP_LINE = String.fromCharCode(0x2028);
const SEP_PARA = String.fromCharCode(0x2029);
const LD_UNSAFE = new RegExp("[<" + SEP_LINE + SEP_PARA + "]", "g");
// One backslash, built from its char code. The replacement values must be
// the six literal characters <, not the character they denote — a
// single-backslash string literal here would compile to "<" and make the
// whole escape a silent no-op.
const BS = String.fromCharCode(92);
const LD_ESCAPE: Record<string, string> = {
  "<": BS + "u003c",
  [SEP_LINE]: BS + "u2028",
  [SEP_PARA]: BS + "u2029",
};

/**
 * Serialise for a `<script type="application/ld+json">` block.
 *
 * `JSON.stringify` does not escape `<`, so a value containing the literal
 * text `</script>` closes the block early and everything after it is parsed
 * as HTML — stored XSS on a marketing page, reachable by anyone who can set a
 * display name. `<` is a valid JSON escape for `<`, so a consumer parses
 * exactly the same object; only the HTML tokenizer sees a difference.
 *
 * U+2028 / U+2029 are escaped too. They are legal in JSON but were illegal in
 * JavaScript string literals before ES2019, and this markup is read by
 * crawlers whose parsers are not all current.
 */
export function ldJson(data: unknown): string {
  return JSON.stringify(data).replace(LD_UNSAFE, (c) => LD_ESCAPE[c] ?? c);
}

/**
 * An href or image src we are willing to emit, or null.
 *
 * The backend's `_URL_RE` (app/api/v1/admin/blog.py) admits `//host`,
 * root-relative and `data:image/`, which is fine for its purposes and too
 * permissive for ours: `//attacker.example/px.png` is an absolute URL in
 * disguise, and rendering it makes every reader of the page request it from
 * the marketing domain, handing that host their IP and user-agent. That is
 * the hotlinking that `blog_sanitize.allowed_image_hosts()` blocks for images
 * inside a post body; a byline avatar must not reintroduce it.
 */
export function safeHref(url: string | null | undefined): string | null {
  const v = (url ?? "").trim();
  if (!v) return null;
  if (v.startsWith("//")) return null; // protocol-relative — absolute, disguised
  if (v.startsWith("/")) return v; // our own origin
  return /^https?:\/\//i.test(v) ? v : null;
}
